import type { UserAffiliateInfo } from "@/types/affiliate";
import { UserPlan } from "./planTypes";
import { Order } from "@/types/orders";
import {
	UserAccountTypes,
	UserAccountUse,
	UserDepartments,
	UserNumberOfEmployees,
	UserOrganizationType,
} from "@/types/user";
import { UserOnlineStatus } from "@/types/chatTypes";

export * from "./businessTypes";
export * from "./planTypes";
export * from "./chatTypes";
export * from "./adminTypes";
export * from "./currency";
export * from "./supportTickets";
export * from "./chatbot";

export interface LoginUser {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
}

export enum SSO {
	google = "Google",
	apple = "Apple",
}

export enum OnboardingStages {
	SSO_INITIAL = "SSO_INITIAL",
	SIGNUP_COMPLETED = "SIGNUP_COMPLETED",
	ACC_VERIFIED = "ACC_VERIFIED",
	PLAN_PURCHASED = "PLAN_PURCHASED",
}

export type User = {
	_id: string;
	email: string;
	role: string;
	firstName: string;
	lastName: string;
	accountType: UserAccountTypes;
	companyName?: string;
	designation?: string;
	department?: UserDepartments;
	organizationType?: UserOrganizationType;
	accountUse?: UserAccountUse;
	numberOfEmployees?: UserNumberOfEmployees;
	mobileNumber: string;
	website?: string;
	avatar?: string;
	onboardingStages: OnboardingStages;
	plan: { duration: UserPlan; planID: string };
	hostingPlan?: { duration: UserPlan; planID: string } | null;
	createdAt: string;
	bio: string;
	onlineStatus: UserOnlineStatus;
	autoStatusEnabled?: boolean;
	lastActiveAt?: string;
	city?: string;
	state?: string;
	country?: string;
	countryCode?: string;
	isConsentAccepted: boolean;

	affiliatedBy: string | null;
	affiliateInfo: UserAffiliateInfo;
	googleId?: string | null;
	appleId?: string | null;
	fcmToken?: string | null;
	fcmPlatform?: "web" | "ios" | "android";
	fcmUnregister?: boolean;
	referralCode?: string | null;
};

export type UserNote = {
	id?: string;
	userId: string;
	note: string;
	createdAt?: string;
};

export type CreateOrderNoteDto = {
	orderId: string;
	authorId: string;
	note: string;
	dataStatus?: DataStatus;
};

export type OrderNote = {
	_id: string;
	orderId: string;
	authorId?: Pick<User, "_id" | "firstName" | "lastName" | "email" | "avatar">;
	note: string;
	dataStatus: DataStatus;
	createdAt: string;
};

export type UpdateUser = Partial<Omit<User, "id" | "_id" | "password" | "createdAt">>;
export type SignUpDTO = Omit<
	User,
	| "id"
	| "_id"
	| "plan"
	| "onboardingStages"
	| "affiliateInfo"
	| "role"
	| "isConsentAccepted"
	| "createdAt"
	| "onlineStatus"
	| "bio"
	| "affiliatedBy"
	| "mobileNumber" // Remove mobileNumber from User type
> & {
	password: string;
	captchaToken?: string; // Optional reCAPTCHA v3 token
	clientType?: "web" | "mobile";
	mobileNumber?: string; // Make mobileNumber optional for signup
};

export type Notification = {
	_id?: string;
	user: User;
	redirectPath: string;
	isRead: boolean;
	content: string;
};

export type FAQ = {
	_id?: string;
	question: string;
	answer: string;
	sortOrder?: number;
	createdAt?: string;
	updatedAt?: string;
};

export type PromoCode = {
	_id: string;
	code: string;
	description?: string;
	discountType: "PERCENTAGE" | "FIXED_AMOUNT";
	discountValue: number;
	currency?: string;
	applyTo: Array<"PACKAGE" | "LICENSE" | "HOSTING" | "PLAN">;
	packageTiers: Array<"BASIC" | "STANDARD" | "PREMIUM">;
	isActive: boolean;
	oneTimePerUser: boolean;
	newUsersOnly: boolean;
	stackableWithOtherPromoCodes?: boolean;
	applyDiscountToDueTodayFirst?: boolean;
	worksWithNewsletter?: boolean;
	allowedEmails: string[];
	maxTotalRedemptions: number | null;
	redemptionCount: number;
	startsAt?: string | null;
	endsAt?: string | null;
	minOrderAmount?: number | null;
	createdAt?: string;
	updatedAt?: string;
};

export type PromoCodeResponse = {
	docs: PromoCode[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
	hasPrevPage: boolean;
	hasNextPage: boolean;
};

export interface Review {
	userId: {
		_id: string;
		username: string;
		email: string;
		firstName: string;
		lastName: string;
		profilecpic: string;
	};
	orderId: Order;
	comment: string;
	rating: number;
	description: string;
	recommended: boolean;
	createdAt: string;
	updatedAt: string;
	_id?: string;
}

export type ErrorResponse = {
	error: string;
	message: string[];
	statusCode: number;
};

export enum DataStatus {
	ACTIVE = "ACTIVE",
	INACTIVE = "INACTIVE",
	DELETED = "DELETED",
}

export enum ServiceStatus {
	PUBLISHED = "PUBLISHED",
	DRAFT = "DRAFT",
	ARCHIVED = "ARCHIVED",
}

export interface CookieConsentData {
	required: boolean;
	performance: boolean;
	advertisements: boolean;
}
